import { CallMadeRounded, CallReceivedRounded } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import { fetchCreditTransfers } from "@/api/credits";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import { useCreditTransfers } from "@/hooks/useCredits";
import type { CreditTransferListItem } from "@/interfaces/credits";

const fmtCredits = (v: string | number) => Number(v).toLocaleString("en-US");
const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** Signed against the group being viewed: what it sent is negative, what it received is positive. */
const signed = (t: CreditTransferListItem) =>
  `${t.direction === "out" ? "−" : "+"}${fmtCredits(t.credits)}`;

/**
 * Credit moved between this group and its siblings.
 *
 * A Blotter like Purchases, and server-paginated for the same reason: a list that loads everything
 * is fine until the day it is not, and by then it is a client's screen that hangs.
 *
 * Its own list rather than rows inside Purchases, because the two are different ledgers — purchases
 * are DOLLARS arriving from a client, transfers are CREDITS already paid for changing hands inside
 * one organisation. One table would need an amount column meaning two things.
 *
 * DIRECTION IS RELATIVE to the group being viewed. The same transfer reads "Sent −1,000 · To
 * Marketing" here and "Received +1,000 · From Test Company" on the other group's list.
 */
export default function GroupTransfersPanel({ groupId }: { groupId: number }) {
  const useData = (q: {
    search: string;
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<CreditTransferListItem> => {
    const { data, isLoading } = useCreditTransfers(groupId, {
      search: q.search || undefined,
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<CreditTransferListItem> = {
    title: "Transfers",
    searchPlaceholder: "Search by group or note",
    itemNoun: "transfer",
    rowKey: (t) => t.id,
    defaultSortKey: "transferred",
    defaultSortDir: "desc",
    filterFields: [],
    useData,
    exportFilename: "transfers.csv",
    exportFetch: async (q) => {
      const res = await fetchCreditTransfers(groupId, {
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    columns: [
      {
        key: "transferred",
        label: "Date",
        sortable: true,
        sortValue: (t) => t.transferredAt,
        render: (t) => dateTime(t.transferredAt),
      },
      {
        key: "direction",
        label: "Direction",
        render: (t) => (
          <Chip
            size="small"
            variant="outlined"
            color={t.direction === "out" ? "warning" : "success"}
            icon={t.direction === "out" ? <CallMadeRounded /> : <CallReceivedRounded />}
            label={t.direction === "out" ? "Sent" : "Received"}
            sx={{ height: 22 }}
          />
        ),
        exportValue: (t) => (t.direction === "out" ? "Sent" : "Received"),
      },
      {
        key: "counterparty",
        label: "Counterparty",
        render: (t) => `${t.direction === "out" ? "To" : "From"} ${t.counterpartyName}`,
        exportValue: (t) => t.counterpartyName,
      },
      {
        key: "credits",
        label: "Credits",
        sortable: true,
        sortValue: (t) => Number(t.credits),
        render: (t) => (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {signed(t)}
          </Box>
        ),
        exportValue: signed,
      },
      { key: "actor", label: "By", render: (t) => t.actorName ?? "—" },
      { key: "note", label: "Note", render: (t) => t.note ?? "—" },
    ],
  };

  return <Blotter config={config} />;
}
